import { supabase } from '../supabase/client'
import { StudentProfile, MatchedScholarship } from '../../features/scholarships/types'

const OPENROUTER_KEY = process.env.REACT_APP_OPENROUTER_API_KEY!
const GEMINI_KEY = process.env.REACT_APP_GEMINI_API_KEY!

async function embedText(text: string): Promise<number[]> {
    const res = await fetch('https://openrouter.ai/api/v1/embeddings', {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${OPENROUTER_KEY}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            model: 'text-embedding-3-small',
            input: text,
        }),
    })
    if (!res.ok) {
        const err = await res.json()
        throw new Error(`Embedding failed: ${JSON.stringify(err)}`)
    }
    const data = await res.json()
    return data.data[0].embedding
}

async function generateText(prompt: string): Promise<string> {
    const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_KEY}`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                contents: [{ role: 'user', parts: [{ text: prompt }] }],
                generationConfig: {
                    temperature: 0.1,
                    maxOutputTokens: 8192,
                },
            }),
        }
    )
    if (!res.ok) {
        const err = await res.json()
        throw new Error(`Generation failed: ${JSON.stringify(err)}`)
    }
    const data = await res.json()
    return data.candidates?.[0]?.content?.parts?.[0]?.text ?? ''
}

function dedupeByName(items: any[]): any[] {
    const seen = new Set<string>()
    return items.filter(item => {
        const key = item.name?.toLowerCase().trim()
        if (!key || seen.has(key)) return false
        seen.add(key)
        return true
    })
}

function clientSideFilter(matches: any[], profile: StudentProfile): any[] {
    return matches.filter(s => {
        // Hard exclude wrong school
        if (s.schools && s.schools.length > 0) {
            const hasAny = s.schools.some((sc: string) =>
                sc.toLowerCase() === 'any' || sc.toLowerCase().includes('any')
            )
            if (!hasAny) {
                const schoolMatch = s.schools.some((sc: string) =>
                    sc.toLowerCase().includes(profile.school.toLowerCase()) ||
                    profile.school.toLowerCase().includes(sc.toLowerCase())
                )
                if (!schoolMatch) return false
            }
        }
        // Hard exclude wrong program level
        if (s.program_types && s.program_types.length > 0) {
            const hasAny = s.program_types.some((pt: string) =>
                pt.toLowerCase() === 'any'
            )
            if (!hasAny) {
                const levelMatch = s.program_types.some((pt: string) =>
                    pt.toLowerCase() === profile.programType.toLowerCase()
                )
                if (!levelMatch) return false
            }
        }
        return true
    })
}

export async function matchScholarships(
    profile: StudentProfile
): Promise<MatchedScholarship[]> {
    const profileText = [
        `Student at ${profile.school}`,
        `Program: ${profile.program}`,
        `Level: ${profile.programType}`,
        profile.incomeMax ? `Income under $${profile.incomeMax}` : '',
        profile.demographicFlags.length > 0
            ? `Background: ${profile.demographicFlags.join(', ')}`
            : '',
    ]
        .filter(Boolean)
        .join('. ')

    let embedding: number[]
    try {
        embedding = await embedText(profileText)
    } catch (e) {
        console.error('Embed error:', e)
        return []
    }

    const { data: matches, error } = await supabase.rpc('match_scholarships', {
        query_embedding: embedding,
        match_count: 20,
    })

    if (error || !matches) {
        console.error('Supabase RPC error:', error)
        return []
    }

    if (matches.length === 0) return []

    // Dedupe before sending to Gemini
    const deduped = dedupeByName(matches)

    const prompt = `
You are a scholarship eligibility checker. Be strict.

Student profile:
- School: ${profile.school}
- Program: ${profile.program}
- Level: ${profile.programType}
- Income max: ${profile.incomeMax ?? 'not specified'}
- Background flags: ${profile.demographicFlags.join(', ') || 'none'}

Scholarships to evaluate (${deduped.length} total):
${JSON.stringify(
        deduped.map((m: any) => ({
            id: m.id,
            name: m.name,
            amount: m.amount,
            deadline: m.deadline,
            description: m.description,
            schools: m.schools ?? [],
            program_types: m.program_types ?? [],
            income_max: m.income_max ?? null,
            demographic_flags: m.demographic_flags ?? [],
        })),
        null,
        2
    )}

Strict rules:
1. If schools array is non-empty and does NOT contain "${profile.school}" or "any", EXCLUDE it
2. If program_types array is non-empty and does NOT contain "${profile.programType}" or "any", EXCLUDE it  
3. Never include duplicates
4. For each INCLUDED scholarship write one short sentence for matchReason

Return ONLY a JSON array. No markdown. No backticks. No explanation.
[{ "id": "...", "matchReason": "..." }]
`

    let filtered: { id: string; matchReason: string }[] = []
    try {
        const raw = await generateText(prompt)
        const clean = raw.replace(/```json|```/g, '').trim()
        filtered = JSON.parse(clean)
    } catch (e) {
        console.error('Gemini filter error:', e)
        // Fallback: client-side filter + dedup, no Gemini
        const fallback = clientSideFilter(deduped, profile)
        return fallback.slice(0, 10).map((m: any) => ({
            ...m,
            matchReason: 'Matched based on your profile',
            similarity: m.similarity,
        }))
    }

    const matchMap = new Map(filtered.map((f: any) => [f.id, f.matchReason]))

    return deduped
        .filter((m: any) => matchMap.has(m.id))
        .map((m: any) => ({
            ...m,
            matchReason: matchMap.get(m.id) ?? '',
        }))
        .slice(0, 10)
}